import React, { useState, useEffect } from 'react';
import { downloadGoogleDriveFileAsBlobUrl } from '../../lib/google-drive';

/**
 * Componente que detecta si la imagen es un ID de Google Drive o base64/URL
 * y la carga apropiadamente
 */
const ImageWithGoogleDrive = ({ 
  src, 
  alt, 
  className = '', 
  fallback,
  width,
  height,
  style = {}
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Resetear estado inmediatamente cuando cambia src
    setImageSrc(null);
    setLoading(true);
    setError(false);

    if (!src) {
      setImageSrc(null);
      setLoading(false);
      return;
    }

    // Si es base64 (data:image), usar directamente
    if (src.startsWith('data:image')) {
      setImageSrc(src);
      setLoading(false);
      setError(false);
      return;
    }

    // Si no es base64, asumimos que es un ID de Google Drive
    let cancelled = false;
    
    console.log('ImageWithGoogleDrive: Attempting to load image with src:', src);
    console.log('ImageWithGoogleDrive: src type:', typeof src);
    console.log('ImageWithGoogleDrive: src length:', src?.length);
    
    downloadGoogleDriveFileAsBlobUrl(src)
      .then((blobUrl) => {
        if (!cancelled) {
          console.log('ImageWithGoogleDrive: Successfully loaded image');
          setImageSrc(blobUrl);
          setLoading(false);
          setError(false);
        }
      })
      .catch((err) => {
        console.error('ImageWithGoogleDrive: Error loading Google Drive image:', err);
        console.error('ImageWithGoogleDrive: File ID:', src);
        console.error('ImageWithGoogleDrive: Error message:', err?.message);
        console.error('ImageWithGoogleDrive: Error stack:', err?.stack);
        if (!cancelled) {
          setError(true);
          setLoading(false);
          setImageSrc(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [src]);

  if (!src) {
    return fallback || null;
  }

  if (error) {
    return fallback || (
      <div 
        className={`d-flex align-items-center justify-content-center rounded bg-light text-muted ${className}`} 
        style={{ 
          minHeight: style.height || '100px',
          minWidth: style.width || '100px',
          ...style 
        }}
      >
        <span className="small">Sin imagen</span>
      </div>
    );
  }

  if (loading || !imageSrc) {
    return (
      <div 
        className={`d-flex align-items-center justify-content-center ${className}`} 
        style={{ 
          minHeight: style.height || '100px',
          minWidth: style.width || '100px',
          ...style 
        }}
      >
        <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}>
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      width={width}
      height={height}
      style={{ display: 'block', ...style }}
      crossOrigin="anonymous"
      onError={(e) => {
        // Si es base64 y falla, puede ser que el tipo MIME esté mal
        // Intentar corregir el tipo MIME si es SVG
        if (imageSrc && imageSrc.startsWith('data:')) {
          const [header, base64Data] = imageSrc.split(',');
          // Si el header dice image/jpeg pero el contenido es XML/SVG, corregirlo
          if (header.includes('image/jpeg') && base64Data) {
            try {
              const decoded = atob(base64Data.substring(0, 100));
              if (decoded.includes('<?xml') || decoded.includes('<svg')) {
                const correctedBase64 = `data:image/svg+xml;base64,${base64Data}`;
                setImageSrc(correctedBase64);
                setError(false);
                return;
              }
            } catch (err) {
              // Ignorar error de decodificación
            }
          }
        }
        
        setError(true);
        setImageSrc(null);
      }}
    />
  );
};

export default ImageWithGoogleDrive;
